import React, { useContext } from 'react';
import { Typography, makeStyles } from '@material-ui/core';
import { parseIDGNotes } from '@mummssoftware/idg';
import { PNGlobalContext } from '@mummssoftware/global-context/PNGlobalContext';
import styled from 'styled-components';
import parse from 'html-react-parser';
import { formatMMDDYYhh } from '@mummssoftware/utils/utils';
import { emptyNoteText, PSO } from '@mummssoftware/utils/constants';
import { ONE_PER_PAGE } from '@mummssoftware/utils/orders';
import { Context } from '../../context';
import { PrintDiv, TitlePrint, NotePSODiv, NoteDiv } from './StyledDiv';
import { PSOSignaturePrint } from './PSOSignature';
import { getTagMap } from '../ProgressNotes/utils/utils';

/* eslint-disable react/no-array-index-key */

type NotesListPrintProps = {
  notes?:
    | mumms.progressNotebyEntity[]
    | Record<string, mumms.progressNotebyEntity[]>;
  isCombined?: boolean;
  isIDG?: boolean;
};
type NotesListProps = {
  notes: mumms.progressNotebyEntity[];
  title?: string;
};

/* Doc:
Long story short, the way this UI (NotesList/PSOSignaturePrint) renders standing orders is that it runs through patient DTOs,
and approvals, and cross references matching PSO & O&N Ids to return what PSOs are related to what O&Ns

ordersGlobalMapEffective date is also very similar to ordersGlobalMap, except it returns an array of objects
whose only key is the shared entity Id, and contains the associated PSO info as the value of said key
and was made to include the "in Effect" date of a PSO in this work as per PO request

this is refactored functionality to fit this use-case; check out other uses of allTags and getTagMap in
the Progress Notes app to get an idea of how this worked before it was refactored here. */

const NotesList = ({ notes }: NotesListProps) => {
  const { patientApprovalsGlobal } = useContext(PNGlobalContext);
  const { printConfig } = useContext(Context);
  const patientApprovals = patientApprovalsGlobal || [];
  // The first element in this array is either "One per page" or "Multi per page"
  // This array is only defined when printing orders
  const printFormat =
    printConfig && Array.isArray(printConfig.view)
      ? (printConfig.view as any)[1]
      : null;

  const useStyles = makeStyles({
    root: {
      fontWeight: 600,
    },
  });

  const classes = useStyles();

  const PageBreak = styled.div`
    @media print {
      page-break-after: always;
    }
  `;

  const printNotes = () =>
    notes.map((note: any, index: number) => {
      interface keyable {
        [key: string]: any;
      }

      let allTags: keyable | null = null;
      if (note.id) {
        allTags = getTagMap(note, patientApprovals);
      }
      /* 

      if there IS alltags[PSO], or there is a PSO entity associated with the note, 
      it gets assigned to standingOrder 

      **
      WHEN DATE FILTERING: 
      the PSO associated to the note gets returned as the value of an "undefined" key 
      inside of allTags, so this code basically double checks for that

      check PR for OP-30030 for screenshot of aforementioned "undefined" key value pair while
      date filtering
      **

      */
      const standingOrder: any =
        allTags &&
        (allTags[PSO] ||
          (Object.values(allTags)[0]?.info &&
          Object.values(allTags)[0]?.info.entitytypeshortname === PSO
            ? Object.values(allTags)[0]
            : null));

      const printNoteNoPSO = (pnNote: any) =>
        pnNote.note &&
        pnNote.note !== emptyNoteText && (
          <div key={`${pnNote.id}-${index}`}>
            <Typography
              variant="body1"
              classes={{
                root: classes.root,
              }}
              id="printNoteDetail"
            >
              {formatMMDDYYhh(pnNote)} - {pnNote.personname}{' '}
              {pnNote.credentials}
            </Typography>
            <NoteDiv>{parse(pnNote.note)}</NoteDiv>
            {printFormat === ONE_PER_PAGE && <PageBreak />}
          </div>
        );

      return (
        <div key={`${note.id}-${index}`}>
          {(standingOrder && (
            <>
              <NotePSODiv standingOrder={standingOrder}>
                <PSOSignaturePrint note={note} standingOrder={standingOrder} />
              </NotePSODiv>
              {printFormat === ONE_PER_PAGE && <PageBreak />}
            </>
          )) ||
            printNoteNoPSO(note)}
        </div>
      );
    });

  return <PrintDiv>{printNotes()}</PrintDiv>;
};

const TitleNoteList = ({ notes, title }: NotesListProps) => (
  <>
    <TitlePrint>{title}</TitlePrint>
    <NotesList notes={notes as mumms.progressNotebyEntity[]} />
  </>
);

export const NotesListPrint = ({
  notes,
  isCombined,
  isIDG,
}: NotesListPrintProps) => {
  if (!notes) return null;
  const category = isIDG ? 'rolegroupname' : 'roleshortname';
  if (!notes) return null;
  if (isCombined) {
    const notesByGroup = Object.entries(notes).reduce((seed: any, n: any) => {
      const note = n[1];
      if (!seed[note[category]]) {
        seed[note[category]] = [note];
      } else {
        seed[note[category]].push(note);
      }
      return seed;
    }, {});
    return (
      <>
        {Object.entries(notesByGroup).map((roleNotes: any) => (
          <TitleNoteList notes={roleNotes[1]} title={roleNotes[0]} />
        ))}
      </>
    );
  }
  return (
    <>
      <NotesList notes={notes as mumms.progressNotebyEntity[]} />
    </>
  );
};

export default NotesListPrint;
